import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, PageBody, EmptyState } from "../components/Shared";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Edit2, Trash2, GitMerge, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", contact_person: "", email: "", phone: "", address: "", notes: "", default_rate_percent: "", re_reel_fee: "" };

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(false);

  const load = () => api.get("/suppliers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => {
    setEditId(s.id);
    setForm({
      name: s.name || "",
      contact_person: s.contact_person || "",
      email: s.email || "",
      phone: s.phone || "",
      address: s.address || "",
      notes: s.notes || "",
      default_rate_percent: s.default_rate_percent == null ? "" : String(s.default_rate_percent),
      re_reel_fee: s.re_reel_fee == null ? "" : String(s.re_reel_fee),
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        default_rate_percent: form.default_rate_percent === "" ? null : parseFloat(form.default_rate_percent),
        re_reel_fee: form.re_reel_fee === "" ? null : parseFloat(form.re_reel_fee),
      };
      if (editId) {
        await api.put(`/suppliers/${editId}`, body);
        toast.success("Supplier updated");
      } else {
        await api.post("/suppliers", body);
        toast.success("Supplier created");
      }
      setOpen(false); setEditId(null); setForm(empty);
      load();
    } catch { toast.error("Failed"); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete supplier ${s.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success("Supplier deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <>
      <PageHeader
        title="Suppliers"
        subtitle="Directory"
        testid="suppliers-title"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none border-slate-300 hover:bg-slate-100" onClick={() => setMergeOpen(true)} data-testid="merge-suppliers-btn" disabled={items.length < 2}>
              <GitMerge className="w-4 h-4 mr-2" /> Merge duplicates
            </Button>
            <Button className="rounded-none bg-blue-600 hover:bg-blue-700 text-white" onClick={openNew} data-testid="new-supplier-btn">
              <Plus className="w-4 h-4 mr-2" /> New Supplier
            </Button>
          </div>
        }
      />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm(empty); } }}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editId ? "Edit supplier" : "New supplier"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {[["name", "Name", true], ["contact_person", "Contact"], ["email", "Email"], ["phone", "Phone"]].map(([k, l, req]) => (
              <div key={k}><Label className="label-caps">{l}</Label>
                <Input required={!!req} className="rounded-none mt-1" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`supplier-${k}`} />
              </div>
            ))}
            <div>
              <Label className="label-caps">Default rate % <span className="text-slate-400 normal-case tracking-normal">(markup added over sub-supplier price)</span></Label>
              <Input type="number" step="any" placeholder="e.g. 6 = +6% markup" className="rounded-none mt-1" value={form.default_rate_percent} onChange={(e) => setForm({ ...form, default_rate_percent: e.target.value })} data-testid="supplier-rate" />
            </div>
            <div>
              <Label className="label-caps">Re-reel fee <span className="text-slate-400 normal-case tracking-normal">(flat per line, added to sub cost when re-reel is on)</span></Label>
              <Input type="number" step="any" placeholder="e.g. 7 = flat $7 fee" className="rounded-none mt-1" value={form.re_reel_fee} onChange={(e) => setForm({ ...form, re_reel_fee: e.target.value })} data-testid="supplier-re-reel-fee" />
            </div>
            <div>
              <Label className="label-caps">Address</Label>
              <Textarea rows={2} className="rounded-none mt-1" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="supplier-address" />
            </div>
            <div>
              <Label className="label-caps">Notes</Label>
              <Textarea rows={2} className="rounded-none mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="supplier-notes" />
            </div>
            <DialogFooter>
              <Button className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="supplier-save-btn" type="submit">
                {editId ? "Save changes" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <PageBody>
        {items.length === 0 ? <EmptyState title="No suppliers yet" hint="Add suppliers to track purchases per item." /> :
          <div className="bg-white border border-slate-300">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-300"><tr className="text-left">
                <th className="py-2 px-3 label-caps">Name</th>
                <th className="py-2 px-3 label-caps text-right">Rate %</th>
                <th className="py-2 px-3 label-caps">Contact</th>
                <th className="py-2 px-3 label-caps">Email</th>
                <th className="py-2 px-3 label-caps">Phone</th>
                <th className="py-2 px-3 w-24 text-right label-caps">Actions</th>
              </tr></thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`supplier-row-${s.id}`}>
                    <td className="py-2 px-3"><Link to={`/suppliers/${s.id}`} className="text-blue-700 hover:text-blue-900 font-medium">{s.name}</Link></td>
                    <td className="py-2 px-3 font-mono text-right">{s.default_rate_percent != null ? `${s.default_rate_percent}%` : "-"}</td>
                    <td className="py-2 px-3">{s.contact_person || "-"}</td>
                    <td className="py-2 px-3 font-mono text-slate-600">{s.email || "-"}</td>
                    <td className="py-2 px-3 font-mono text-slate-600">{s.phone || "-"}</td>
                    <td className="py-2 px-3 text-right">
                      <div className="inline-flex gap-2">
                        <button className="text-slate-400 hover:text-blue-700" onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`} title="Edit"><Edit2 className="w-4 h-4" /></button>
                        <button className="text-slate-400 hover:text-red-600" onClick={() => del(s)} data-testid={`del-supplier-${s.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </PageBody>

      <MergeSuppliersDialog open={mergeOpen} onClose={() => setMergeOpen(false)} suppliers={items} onDone={load} />
    </>
  );
}

function MergeSuppliersDialog({ open, onClose, suppliers, onDone }) {
  const [primaryId, setPrimaryId] = useState("");
  const [dupIds, setDupIds] = useState({});
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPrimaryId(""); setDupIds({}); setQ("");
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return suppliers;
    return suppliers.filter((x) => (x.name || "").toLowerCase().includes(s));
  }, [suppliers, q]);

  const selectedDups = Object.keys(dupIds).filter((k) => dupIds[k] && k !== primaryId);
  const primary = suppliers.find((s) => s.id === primaryId);

  const submit = async () => {
    if (!primaryId) return toast.error("Primary supplier seçin");
    if (!selectedDups.length) return toast.error("En az bir duplicate seçin");
    if (!window.confirm(
      `${selectedDups.length} tedarikçi "${primary?.name}" içinde birleştirilecek. Bağlı tüm order/offer/shipment kalemleri primary'ye taşınacak ve duplicate kayıtlar silinecek. Devam?`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/suppliers/merge", {
        primary_id: primaryId,
        duplicate_ids: selectedDups,
      });
      toast.success(
        `Birleştirildi: ${data.duplicates_deleted} silindi · ${data.orders_touched} order · ${data.offers_touched} offer · ${data.shipments_touched} shipment`,
        { duration: 6000 },
      );
      onClose();
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Birleştirilemedi");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none max-w-2xl" data-testid="merge-suppliers-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <GitMerge className="w-5 h-5 text-blue-700" /> Tedarikçi birleştirme
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Duplicate tedarikçileri seçilen "primary" tedarikçide toplayın. Tüm order, offer ve shipment kalemleri primary'ye bağlanır; duplicate kayıtlar silinir. Bu işlem geri alınamaz.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-caps">1. Primary supplier</Label>
            <Select value={primaryId} onValueChange={(v) => { setPrimaryId(v); setDupIds((d) => ({ ...d, [v]: false })); }}>
              <SelectTrigger className="rounded-none mt-1" data-testid="merge-primary-select">
                <SelectValue placeholder="Hangi tedarikçi kalsın?" />
              </SelectTrigger>
              <SelectContent className="rounded-none max-h-72">
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`merge-primary-opt-${s.id}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">2. Silinecek duplicate'ler</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ada göre filtrele…" className="rounded-none mt-1" data-testid="merge-search" />
            <div className="mt-2 border border-slate-300 max-h-64 overflow-y-auto bg-white">
              {filtered.length === 0 ? (
                <div className="p-3 text-xs text-slate-500">Sonuç yok</div>
              ) : filtered.map((s) => {
                const isPrimary = s.id === primaryId;
                return (
                  <label key={s.id} className={`flex items-center gap-2 px-3 py-2 border-b border-slate-100 text-sm ${isPrimary ? "opacity-40" : "hover:bg-slate-50 cursor-pointer"}`}>
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      disabled={isPrimary || !primaryId}
                      checked={!!dupIds[s.id]}
                      onChange={(e) => setDupIds((d) => ({ ...d, [s.id]: e.target.checked }))}
                      data-testid={`merge-dup-${s.id}`}
                    />
                    <span className={`font-mono ${isPrimary ? "text-blue-700 font-medium" : ""}`}>{s.name}</span>
                    {isPrimary && <span className="text-[10px] tracking-widest uppercase text-blue-700">(primary)</span>}
                  </label>
                );
              })}
            </div>
          </div>
          {selectedDups.length > 0 && primary && (
            <div className="border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex gap-2 items-start" data-testid="merge-summary">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <b>{selectedDups.length}</b> tedarikçi silinip <b>{primary.name}</b> içinde toplanacak. Tüm bağlı order/offer/shipment kalemleri primary'ye taşınacak.
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="merge-cancel">Vazgeç</Button>
          <Button onClick={submit} disabled={busy || !primaryId || !selectedDups.length} className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="merge-submit">
            <GitMerge className="w-4 h-4 mr-2" /> {selectedDups.length ? `${selectedDups.length} kaydı birleştir` : "Birleştir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
